import React from "react";
import { Box, Grid, Card, CardContent, Typography, Chip } from "@mui/material";
import { ChatSessionDto } from "@/apis/types/SuperChatLogTypes";

interface SessionInfoTabProps {
  session: ChatSessionDto;
}

/**
 * 세션 정보 탭 컴포넌트
 *
 * @description 세션의 기본 정보와 시간 정보를 표시하는 탭
 * @author Max <sud665@ymjlab.com>
 * @version 1.0.0
 * @since 2025-01-16
 */
export const SessionInfoTab: React.FC<SessionInfoTabProps> = ({ session }) => {
  return (
    <Box sx={{ p: 3 }}>
      <Grid container spacing={3}>
        {/* 기본 정보 카드 */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                기본 정보
              </Typography>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    세션 타입:
                  </Typography>
                  <Chip label={session.sessionType} size="small" />
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    상태:
                  </Typography>
                  <Chip
                    label={session.sessionStatus}
                    size="small"
                    color={
                      session.sessionStatus === "ACTIVE" ? "success" : "default"
                    }
                  />
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    우선순위:
                  </Typography>
                  <Chip
                    label={session.priorityLevel || "NORMAL"}
                    size="small"
                    variant="outlined"
                  />
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    진행 상태:
                  </Typography>
                  <Chip
                    label={session.progressStatus || "진행중"}
                    size="small"
                  />
                </Box>

                {session.primaryCategory && (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      카테고리:
                    </Typography>
                    <Chip
                      label={session.primaryCategory}
                      size="small"
                      variant="outlined"
                      color="primary"
                    />
                  </Box>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* 시간 정보 카드 */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                시간 정보
              </Typography>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                  <Typography variant="body2" color="text.secondary">
                    시작 시간:
                  </Typography>
                  <Typography variant="body2">
                    {new Date(session.startedAt).toLocaleString("ko-KR")}
                  </Typography>
                </Box>

                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                  <Typography variant="body2" color="text.secondary">
                    마지막 활동:
                  </Typography>
                  <Typography variant="body2">
                    {new Date(session.lastActivityAt).toLocaleString("ko-KR")}
                  </Typography>
                </Box>

                {session.endedAt && (
                  <Box
                    sx={{ display: "flex", justifyContent: "space-between" }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      종료 시간:
                    </Typography>
                    <Typography variant="body2">
                      {new Date(session.endedAt).toLocaleString("ko-KR")}
                    </Typography>
                  </Box>
                )}

                {session.regDttm && (
                  <Box
                    sx={{ display: "flex", justifyContent: "space-between" }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      등록 시간:
                    </Typography>
                    <Typography variant="body2">
                      {new Date(session.regDttm).toLocaleString("ko-KR")}
                    </Typography>
                  </Box>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

